import { useMemo } from "react";
import type { DiscoverItem } from "../types";
import type { ViewportBounds } from "../state/discover-reducer";
import { useDiscoverContext } from "../state/discover-context";
import { useFilteredItems } from "./use-filtered-items";

export interface InViewResult {
  /** Items to show in the list — full filtered set, or the in-view subset. */
  readonly items: readonly DiscoverItem[];
  /** True when the map viewport is narrowing the list ("places in view"). */
  readonly active: boolean;
}

/** Narrow a filtered item set to a viewport, sorted by distance from its centre. */
export function inViewItems(
  filtered: readonly DiscoverItem[],
  bounds: ViewportBounds | null,
): InViewResult {
  if (!bounds) return { items: filtered, active: false };
  const within = filtered.filter(
    (it) =>
      it.latitude != null &&
      it.longitude != null &&
      it.latitude <= bounds.north &&
      it.latitude >= bounds.south &&
      it.longitude <= bounds.east &&
      it.longitude >= bounds.west,
  );
  // Squared-euclidean distance from the map centre — cheaper than haversine and
  // identical ordering over a single viewport's small lat/lng span.
  const dist = (it: DiscoverItem) =>
    (it.latitude! - bounds.centerLat) ** 2 + (it.longitude! - bounds.centerLng) ** 2;
  const sorted = [...within].sort((a, b) => dist(a) - dist(b));
  return { items: sorted, active: true };
}

/**
 * The list's items, narrowed to the live map viewport when viewport-sync is
 * active (the map has published bounds) and sorted nearest-first. Falls back to
 * the full filtered browse set when zoomed out.
 */
export function useInViewItems(): InViewResult {
  const { state } = useDiscoverContext();
  const filtered = useFilteredItems();
  const bounds = state.viewportBounds;
  return useMemo(() => inViewItems(filtered, bounds), [filtered, bounds]);
}
